import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppAuth } from './AppAuthContext';
import { Button } from '../../components/ui/button';
import { 
  Clock, MapPin, LogOut, Fingerprint, History, 
  Wallet, Calendar, ChevronRight, Loader2, CheckCircle,
  User, Building2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppHome = () => {
  const navigate = useNavigate();
  const { user, token, logout, isAuthenticated, authHeaders } = useAppAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marcacoesHoje, setMarcacoesHoje] = useState([]);
  const [bancoHoras, setBancoHoras] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrando, setRegistrando] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/app/login');
      return;
    }

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isAuthenticated, navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const [marcacoesRes, bancoRes] = await Promise.all([
        fetch(`${API_URL}/api/app/marcacoes-hoje`, { headers: authHeaders }),
        fetch(`${API_URL}/api/app/banco-horas`, { headers: authHeaders })
      ]);

      if (marcacoesRes.ok) {
        const data = await marcacoesRes.json();
        setMarcacoesHoje(data.marcacoes || []);
      }

      if (bancoRes.ok) {
        const data = await bancoRes.json();
        setBancoHoras(data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  }, [token, authHeaders]);

  useEffect(() => {
    loadData();
    getLocation();
  }, [loadData]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Erro ao obter localização:', error);
        }
      );
    }
  };

  const registrarPonto = async () => {
    setRegistrando(true);
    try {
      const response = await fetch(`${API_URL}/api/app/registrar-ponto`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          latitude: location?.latitude,
          longitude: location?.longitude
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Ponto registrado às ${data.marcacao.hora}`);
        setMarcacoesHoje(data.marcacoes_dia || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao registrar ponto');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setRegistrando(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/app/login');
  };

  const formatSaldoBancoHoras = () => {
    if (!bancoHoras) return '0h00';
    const saldo = bancoHoras.saldo_total_horas || 0;
    const horas = Math.floor(Math.abs(saldo));
    const minutos = Math.round((Math.abs(saldo) - horas) * 60);
    const sinal = saldo >= 0 ? '+' : '-';
    return `${sinal}${horas}h${minutos.toString().padStart(2, '0')}`;
  };

  const getMarcacaoLabel = (index) => {
    const labels = ['Entrada', 'Saída Intervalo', 'Retorno', 'Saída'];
    return labels[index] || `Marcação ${index + 1}`;
  };

  const getStatusJornada = () => {
    const total = marcacoesHoje.length;
    if (total === 0) return { text: 'Aguardando entrada', color: 'text-amber-400', bg: 'bg-amber-400/10' };
    if (total === 1) return { text: 'Trabalhando', color: 'text-emerald-400', bg: 'bg-emerald-400/10' };
    if (total === 2) return { text: 'Em intervalo', color: 'text-blue-400', bg: 'bg-blue-400/10' };
    if (total === 3) return { text: 'Trabalhando', color: 'text-emerald-400', bg: 'bg-emerald-400/10' };
    return { text: 'Jornada finalizada', color: 'text-slate-400', bg: 'bg-slate-400/10' };
  };

  const status = getStatusJornada();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Jornyx</h1>
              <p className="text-xs text-slate-400">{user?.empresa_nome}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={loadData}
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 overflow-y-auto">
        {/* User Info */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-4 mb-4 border border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold">{user?.nome}</h2>
              <p className="text-slate-400 text-sm">{user?.cargo || user?.matricula}</p>
            </div>
            <div className={`px-3 py-1 rounded-full ${status.bg}`}>
              <span className={`text-xs font-medium ${status.color}`}>{status.text}</span>
            </div>
          </div>
        </div>

        {/* Clock & Register */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 mb-4 border border-white/10 shadow-xl">
          <div className="text-center mb-6">
            <div className="text-6xl font-bold text-white font-mono tracking-wider">
              {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-2xl text-slate-500 font-mono">
              :{currentTime.getSeconds().toString().padStart(2, '0')}
            </div>
            <p className="text-slate-400 mt-2">
              {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>

          {/* Location Status */}
          <div className={`flex items-center justify-center gap-2 mb-4 px-4 py-2 rounded-full mx-auto w-fit ${
            location ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
          }`}>
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{location ? 'Localização obtida' : 'Sem localização'}</span>
          </div>

          {/* Register Button */}
          <Button
            onClick={registrarPonto}
            disabled={registrando}
            className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-emerald-500/30"
          >
            {registrando ? (
              <>
                <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Fingerprint className="w-6 h-6 mr-2" />
                Registrar Ponto
              </>
            )}
          </Button>
        </div>

        {/* Today's Markings */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-4 mb-4 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Marcações de Hoje
            </h3>
            <span className="text-slate-400 text-sm">{marcacoesHoje.length} registro(s)</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : marcacoesHoje.length === 0 ? (
            <div className="text-center py-6">
              <Clock className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500">Nenhuma marcação hoje</p>
            </div>
          ) : (
            <div className="space-y-2">
              {marcacoesHoje.map((marcacao, index) => (
                <div
                  key={marcacao.id || index}
                  className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3"
                >
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">{marcacao.hora}</p>
                    <p className="text-slate-400 text-sm">{getMarcacaoLabel(index)}</p>
                  </div>
                  <span className="text-xs text-slate-500 uppercase">{marcacao.origem}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => toast.info('Banco de Horas: ' + formatSaldoBancoHoras())}
            className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 text-left"
          >
            <Wallet className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-xs text-slate-400">Banco de Horas</p>
            <p className={`text-lg font-bold ${
              (bancoHoras?.saldo_total_horas || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {formatSaldoBancoHoras()}
            </p>
          </button>

          <button
            onClick={() => toast.info('Funcionalidade em desenvolvimento')}
            className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 text-left"
          >
            <Calendar className="w-6 h-6 text-amber-400 mb-2" />
            <p className="text-xs text-slate-400">Minha Escala</p>
            <p className="text-sm text-white font-medium mt-1 flex items-center">
              Ver turnos
              <ChevronRight className="w-4 h-4 ml-1" />
            </p>
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center border-t border-white/10">
        <p className="text-slate-600 text-xs">Jornyx PWA v1.0 - G2 Soluções</p>
      </footer>
    </div>
  );
};

export default AppHome;
